import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Search, AlertTriangle } from 'lucide-react';
import { apiClient } from '../../utils/apiHelper';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StockReport = () => {
  const [report, setReport] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredItems, setFilteredItems] = useState([]);

  useEffect(() => {
    fetchReport();
  }, []);

  useEffect(() => {
    if (report && report.items) {
      if (searchTerm) {
        const filtered = report.items.filter(item =>
          item.item_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.category?.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredItems(filtered);
      } else {
        setFilteredItems(report.items);
      }
    }
  }, [searchTerm, report]);

  const fetchReport = async () => {
    try {
      const response = await apiClient.get('/api/inventory/reports/stock');
      const data = Array.isArray(response.data) ? response.data : response.data;
      setReport(data);
      setFilteredItems(data.items || []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  if (!report) return <div className="p-6">Loading...</div>;

  return (
    <div className="p-6 space-y-6">
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="text-sm text-gray-600">Total Items</div>
            <div className="text-3xl font-bold">{report.total_items}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="text-sm text-gray-600">Total Stock Value</div>
            <div className="text-3xl font-bold text-green-600">₹{report.total_value?.toFixed(2)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="text-sm text-gray-600 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-600" />
              Low Stock Items
            </div>
            <div className="text-3xl font-bold text-red-600">{report.low_stock_count}</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Stock Report</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Search items..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3">Item Name</th>
                  <th className="text-left p-3">Code</th>
                  <th className="text-left p-3">Category</th>
                  <th className="text-left p-3">Unit</th>
                  <th className="text-left p-3">Quantity</th>
                  <th className="text-left p-3">Min Stock</th>
                  <th className="text-left p-3">Unit Price</th>
                  <th className="text-left p-3">Total Value</th>
                  <th className="text-left p-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.length === 0 ? (
                  <tr><td colSpan="9" className="text-center p-4 text-gray-500">No items found.</td></tr>
                ) : (
                  filteredItems.map((item) => {
                    const isLowStock = item.quantity <= item.minimum_stock;
                    return (
                      <tr key={item.id} className={`border-b hover:bg-gray-50 ${isLowStock ? 'bg-red-50' : ''}`}>
                        <td className="p-3 font-medium">{item.item_name}</td>
                        <td className="p-3">{item.item_code}</td>
                        <td className="p-3">{item.category}</td>
                        <td className="p-3">{item.unit}</td>
                        <td className="p-3">
                          <span className={isLowStock ? 'text-red-600 font-bold' : ''}>{item.quantity}</span>
                        </td>
                        <td className="p-3">{item.minimum_stock}</td>
                        <td className="p-3">₹{item.unit_price}</td>
                        <td className="p-3 font-medium">₹{item.total_value}</td>
                        <td className="p-3">
                          {isLowStock ? (
                            <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800 flex items-center gap-1 w-fit">
                              <AlertTriangle className="w-3 h-3" />
                              Low Stock
                            </span>
                          ) : (
                            <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">In Stock</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default StockReport;